import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteFooter from './sections/SiteFooter';
import { LEGAL } from './constants';
import { LanguageProvider, useLanguage } from './LanguageContext';
import './PitboardSite.css';

// ─── Composants réutilisables ─────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <div className="pb-legal-row">
      <span className="pb-legal-row-label">{label}</span>
      <span className="pb-legal-row-value">{value}</span>
    </div>
  );
}

function LegalSection({ id, title, children }) {
  return (
    <section className="pb-legal-section" id={id}>
      <h2 className="pb-legal-h2">{title}</h2>
      {children}
    </section>
  );
}

function Article({ num, title, children }) {
  return (
    <article className="pb-legal-article">
      <h3 className="pb-legal-h3">Article {num} — {title}</h3>
      <div className="pb-legal-body">{children}</div>
    </article>
  );
}

// ─── Contenu légal bilingue ───────────────────────────────────────────────────
const LEGAL_UI = {
  fr: {
    backBtn:   'Retour au site',
    pageLabel: 'Informations légales',
    pageTitle: 'Mentions légales & CGU',
    docTitle:  'Mentions légales & CGU — PitBoard Suite | Logiciel de gestion assurance',
    intro:     (year) => `Dernière mise à jour : ${year}. Ces informations concernent le site pitboardsuite.com / pitboardsuite.fr et le service PitBoard Suite, édités par JOERKE.B.`,
    legalNote: null,
    sections: {
      legal: { id: 'mentions-legales', title: 'Mentions légales' },
      cgu:   { id: 'cgu',              title: "Conditions Générales d'Utilisation (CGU)" },
    },
    publisherRows: (l) => [
      { label: 'Raison sociale',                 value: l.company   },
      { label: 'Forme juridique',                value: l.form      },
      { label: 'Capital social',                 value: l.capital   },
      { label: 'SIREN',                          value: l.siren     },
      { label: 'SIRET',                          value: l.siret     },
      { label: 'RCS',                            value: l.rcs       },
      { label: 'N° TVA intracommunautaire',      value: l.tva       },
      { label: 'Siège social',                   value: l.address   },
      { label: 'Président',                      value: l.president },
      { label: 'Directeur de la publication',    value: l.publication},
      { label: 'Contact',                        value: l.email     },
    ],
    hosting:    'Hébergement',
    ip:         'Propriété intellectuelle',
    ipText1:    "L'ensemble du contenu de ce site (textes, images, graphismes, logo, icônes, logiciels, etc.) est la propriété exclusive de JOERKE.B ou de ses partenaires. Toute reproduction, représentation, modification, publication, adaptation ou exploitation, même partielle, est strictement interdite sans accord écrit préalable de JOERKE.B.",
    ipText2:    "Les marques et logos reproduits sur ce site sont des marques déposées par leurs titulaires respectifs.",
    data:       'Données personnelles',
    dataText1:  "Conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi Informatique et Libertés du 6 janvier 1978 modifiée, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition sur vos données personnelles.",
    dataText2:  (email) => `Pour exercer ces droits ou pour toute question relative à vos données, contactez-nous à :`,
    dataText3:  "Les données collectées via les formulaires du site sont utilisées uniquement dans le cadre de la relation commerciale avec JOERKE.B et ne sont pas transmises à des tiers sans votre consentement.",
    cookies:    'Cookies',
    cookiesText:"Ce site peut utiliser des cookies techniques nécessaires à son fonctionnement. Aucun cookie publicitaire ou de traçage tiers n'est déposé sans votre consentement explicite. Vous pouvez paramétrer votre navigateur pour refuser les cookies à tout moment.",
    liability:  'Limitation de responsabilité',
    liabText1:  "JOERKE.B s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, JOERKE.B ne saurait être tenu responsable des omissions, inexactitudes ou carences dans la mise à jour des informations, ni des dommages directs ou indirects résultant de l'utilisation de ce site.",
    liabText2:  "Les liens hypertextes vers d'autres sites ne sauraient engager la responsabilité de JOERKE.B quant au contenu de ces sites.",
    cguIntro:   "Les présentes Conditions Générales d'Utilisation régissent l'accès et l'utilisation du service PitBoard Suite, édité par JOERKE.B. En accédant au service, l'utilisateur reconnaît avoir lu, compris et accepté sans réserve les présentes CGU.",
    articles: [
      { num: '1', title: "Objet",
        text: ["PitBoard Suite est un Business Operating System (BOS) à destination des professionnels du secteur de l'assurance (cabinets de courtage, sociétés d'assistance, compagnies d'assurance). Il permet la gestion centralisée des clients, contrats, dossiers, devis, factures, messagerie et agenda au sein d'une plateforme SaaS sécurisée."] },
      { num: '2', title: "Accès au service",
        text: ["L'accès au service est réservé aux utilisateurs autorisés disposant d'identifiants valides. Toute utilisation du service implique la création d'un compte ou l'attribution d'un accès par un administrateur autorisé. L'utilisateur est seul responsable de la confidentialité de ses identifiants.",
               "JOERKE.B se réserve le droit de suspendre ou résilier tout accès en cas d'utilisation frauduleuse, abusive ou contraire aux présentes CGU."] },
      { num: '3', title: "Utilisation du service", intro: "L'utilisateur s'engage à utiliser le service conformément à sa destination et à la réglementation en vigueur. Il est notamment interdit de :",
        list: ["Utiliser le service à des fins illicites ou frauduleuses ;","Tenter d'accéder à des données appartenant à d'autres utilisateurs ou tenants ;","Reverse-engineer, décompiler ou altérer les composants du service ;","Transmettre des contenus portant atteinte aux droits de tiers ;","Surcharger les infrastructures de manière excessive et délibérée."] },
      { num: '4', title: "Données et confidentialité",
        text: ["JOERKE.B s'engage à traiter les données personnelles et professionnelles des utilisateurs dans le strict respect du RGPD. Les données sont hébergées en France ou dans l'Union Européenne et ne font l'objet d'aucune cession à des tiers sans consentement explicite.",
               "L'architecture multi-tenant de PitBoard Suite garantit l'isolation totale des données entre les différents clients. Chaque espace est cloisonné et accessible uniquement par les utilisateurs autorisés.",
               "Des sauvegardes régulières sont réalisées. JOERKE.B met en œuvre les mesures techniques et organisationnelles appropriées pour assurer la sécurité des données."] },
      { num: '5', title: "Propriété intellectuelle",
        text: ["Le service PitBoard Suite, son code, ses interfaces, ses algorithmes et l'ensemble des éléments le composant sont la propriété exclusive de JOERKE.B. Aucun droit de propriété intellectuelle n'est transféré à l'utilisateur dans le cadre du présent contrat.",
               "L'utilisateur conserve la pleine propriété des données qu'il renseigne dans le service."] },
      { num: '6', title: "Disponibilité et maintenance",
        text: ["JOERKE.B s'efforce d'assurer la disponibilité du service 24h/24, 7j/7. Des interruptions planifiées pour maintenance peuvent survenir, avec information préalable des utilisateurs dans la mesure du possible. JOERKE.B ne saurait être tenu responsable des interruptions indépendantes de sa volonté (pannes opérateurs, cas de force majeure)."] },
      { num: '7', title: "Responsabilité",
        text: ["JOERKE.B est soumis à une obligation de moyens et non de résultat. Sa responsabilité ne saurait être engagée pour tout dommage indirect résultant de l'utilisation ou de l'impossibilité d'utiliser le service. En tout état de cause, la responsabilité de JOERKE.B est limitée au montant des sommes effectivement versées par l'utilisateur sur les 12 derniers mois."] },
      { num: '8', title: "Modification des CGU",
        text: ["JOERKE.B se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés de toute modification substantielle par voie électronique (email ou notification in-app). La poursuite de l'utilisation du service après notification vaut acceptation des nouvelles conditions."] },
      { num: '9', title: "Droit applicable et juridiction",
        text: ["Les présentes CGU sont soumises au droit français. En cas de litige, et à défaut de résolution amiable, les tribunaux compétents du ressort du siège social de JOERKE.B (Troyes, France) seront seuls compétents."] },
    ],
    footer: (year, siren, address) => `© ${year} PitBoard Suite — JOERKE.B, SIREN ${siren} — ${address}`,
  },
  en: {
    backBtn:   'Back to site',
    pageLabel: 'Legal information',
    pageTitle: 'Legal Notice & Terms of Use',
    docTitle:  'Legal Notice & Terms of Use — PitBoard Suite | Insurance Management Software',
    intro:     (year) => `Last updated: ${year}. This information relates to the websites pitboardsuite.com / pitboardsuite.fr and the PitBoard Suite service, published by JOERKE.B.`,
    legalNote: 'Note: This is a translation of the original French legal document provided for informational purposes. The French version is the legally binding version and takes precedence.',
    sections: {
      legal: { id: 'mentions-legales', title: 'Legal Notice' },
      cgu:   { id: 'cgu',              title: 'Terms of Use (ToU)' },
    },
    publisherRows: (l) => [
      { label: 'Company name',         value: l.company   },
      { label: 'Legal form',           value: 'SASU — Simplified joint-stock company (French law)' },
      { label: 'Share capital',        value: l.capital   },
      { label: 'SIREN',                value: l.siren     },
      { label: 'SIRET',                value: l.siret     },
      { label: 'RCS',                  value: l.rcs       },
      { label: 'VAT number',           value: l.tva       },
      { label: 'Registered office',    value: l.address   },
      { label: 'CEO',                  value: l.president },
      { label: 'Publication director', value: l.publication},
      { label: 'Contact',              value: l.email     },
    ],
    hosting:    'Hosting',
    ip:         'Intellectual property',
    ipText1:    'All content on this website (text, images, graphics, logo, icons, software, etc.) is the exclusive property of JOERKE.B or its partners. Any reproduction, representation, modification, publication, adaptation or exploitation, even partial, is strictly prohibited without prior written consent from JOERKE.B.',
    ipText2:    'Trademarks and logos reproduced on this site are registered trademarks owned by their respective holders.',
    data:       'Personal data',
    dataText1:  'In accordance with the General Data Protection Regulation (GDPR — EU 2016/679) and the French Data Protection Act of 6 January 1978, you have the right of access, rectification, erasure, portability and objection concerning your personal data.',
    dataText2:  (email) => `To exercise these rights or for any question relating to your data, contact us at:`,
    dataText3:  'Data collected via the website forms is used solely in the context of the commercial relationship with JOERKE.B and is not transmitted to third parties without your consent.',
    cookies:    'Cookies',
    cookiesText:'This website may use technical cookies necessary for its operation. No advertising or third-party tracking cookies are placed without your explicit consent. You can configure your browser to refuse cookies at any time.',
    liability:  'Limitation of liability',
    liabText1:  'JOERKE.B strives to ensure the accuracy and currency of information published on this website. However, JOERKE.B cannot be held liable for omissions, inaccuracies or shortcomings in updating information, nor for direct or indirect damages resulting from the use of this website.',
    liabText2:  'Hyperlinks to other websites shall not engage the liability of JOERKE.B with respect to the content of those websites.',
    cguIntro:   'These Terms of Use govern access to and use of the PitBoard Suite service, published by JOERKE.B. By accessing the service, the user acknowledges having read, understood and accepted these ToU without reservation.',
    articles: [
      { num: '1', title: 'Purpose',
        text: ['PitBoard Suite is a Business Operating System (BOS) designed for insurance industry professionals (brokerage firms, assistance companies, insurance companies). It enables centralized management of clients, contracts, files, quotes, invoices, messaging and calendar within a secure SaaS platform.'] },
      { num: '2', title: 'Access to the service',
        text: ['Access to the service is reserved for authorized users holding valid credentials. Any use of the service involves creating an account or being granted access by an authorized administrator. The user is solely responsible for the confidentiality of their credentials.',
               'JOERKE.B reserves the right to suspend or terminate any access in the event of fraudulent, abusive or non-compliant use.'] },
      { num: '3', title: 'Use of the service', intro: 'The user agrees to use the service in accordance with its purpose and applicable regulations. In particular, it is prohibited to:',
        list: ['Use the service for illegal or fraudulent purposes;','Attempt to access data belonging to other users or tenants;','Reverse-engineer, decompile or alter service components;','Transmit content infringing third-party rights;','Overload infrastructure excessively and deliberately.'] },
      { num: '4', title: 'Data and confidentiality',
        text: ['JOERKE.B undertakes to process users\' personal and professional data in strict compliance with GDPR. Data is hosted in France or in the European Union and is not sold to third parties without explicit consent.',
               'PitBoard Suite\'s multi-tenant architecture guarantees total data isolation between different clients. Each space is compartmentalized and accessible only to authorized users.',
               'Regular backups are performed. JOERKE.B implements appropriate technical and organizational measures to ensure data security.'] },
      { num: '5', title: 'Intellectual property',
        text: ['The PitBoard Suite service, its code, interfaces, algorithms and all components are the exclusive property of JOERKE.B. No intellectual property rights are transferred to the user under this agreement.',
               'The user retains full ownership of the data they enter into the service.'] },
      { num: '6', title: 'Availability and maintenance',
        text: ['JOERKE.B strives to ensure service availability 24/7. Planned maintenance interruptions may occur, with prior notice to users where possible. JOERKE.B cannot be held liable for interruptions beyond its control (operator failures, force majeure).'] },
      { num: '7', title: 'Liability',
        text: ['JOERKE.B is subject to a best-efforts obligation. Its liability cannot be engaged for any indirect damage resulting from the use or inability to use the service. In any case, JOERKE.B\'s liability is limited to the amounts actually paid by the user over the preceding 12 months.'] },
      { num: '8', title: 'Modification of ToU',
        text: ['JOERKE.B reserves the right to modify these ToU at any time. Users will be informed of any substantial modification electronically (email or in-app notification). Continued use of the service after notification constitutes acceptance of the new terms.'] },
      { num: '9', title: 'Governing law and jurisdiction',
        text: ['These ToU are governed by French law. In the event of a dispute and in the absence of amicable resolution, the competent courts of JOERKE.B\'s registered office jurisdiction (Troyes, France) shall have exclusive jurisdiction.'] },
    ],
    footer: (year, siren, address) => `© ${year} PitBoard Suite — JOERKE.B, SIREN ${siren} — ${address}`,
  },
};

// ─── Contenu principal ────────────────────────────────────────────────────────
function LegalContent() {
  const navigate  = useNavigate();
  const { lang }  = useLanguage();
  const ui        = LEGAL_UI[lang];
  const year      = new Date().getFullYear();

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Inter:wght@300;400;500&display=swap';
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title  = ui.docTitle;
    return () => { document.title = prevTitle; };
  }, [ui]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pitboard-site" data-testid="pb-legal-page">
      <header className="pb-nav scrolled" style={{ position: 'sticky', top: 0 }} data-testid="pb-legal-nav">
        <div className="pb-nav-inner">
          <button className="pb-legal-back-btn" onClick={() => navigate('/pitboard-suite')} data-testid="pb-legal-back-btn">
            <ArrowLeft size={16} />
            {ui.backBtn}
          </button>
          <span className="pb-nav-logo" style={{ cursor: 'default' }}>
            <span className="pb-nav-logo-name">PitBoard<span>.</span></span>
          </span>
          <div />
        </div>
      </header>

      <main className="pb-legal-main">
        <div className="pb-container pb-legal-container">
          <div className="pb-legal-page-header">
            <div className="pb-label">{ui.pageLabel}</div>
            <h1 className="pb-legal-h1">{ui.pageTitle}</h1>
            <p className="pb-legal-intro">{ui.intro(year)}</p>
            {ui.legalNote && (
              <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 8, fontStyle: 'italic' }}>{ui.legalNote}</p>
            )}
          </div>

          {/* ── Section Mentions légales ── */}
          <LegalSection id={ui.sections.legal.id} title={ui.sections.legal.title}>
            <h3 className="pb-legal-h3" style={{ marginTop: 0 }}>{lang === 'fr' ? 'Éditeur du site' : 'Website publisher'}</h3>
            <div className="pb-legal-card">
              {ui.publisherRows(LEGAL).map(r => <InfoRow key={r.label} label={r.label} value={r.value} />)}
            </div>

            <h3 className="pb-legal-h3">{ui.hosting}</h3>
            <div className="pb-legal-card">
              {LEGAL.hosting.map((h, i) => (
                <div key={i} className={i < LEGAL.hosting.length - 1 ? 'pb-legal-host-row' : undefined}>
                  <div className="pb-legal-row-label">{h.name}</div>
                  <div className="pb-legal-row-value">
                    {h.address}{' '}
                    <a href={h.url} target="_blank" rel="noopener noreferrer" className="pb-legal-link">{h.url}</a>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="pb-legal-h3">{ui.ip}</h3>
            <div className="pb-legal-body">
              <p>{ui.ipText1}</p>
              <p>{ui.ipText2}</p>
            </div>

            <h3 className="pb-legal-h3">{ui.data}</h3>
            <div className="pb-legal-body">
              <p>{ui.dataText1}</p>
              <p>{ui.dataText2(LEGAL.email)} <a href={`mailto:${LEGAL.email}`} className="pb-legal-link">{LEGAL.email}</a></p>
              <p>{ui.dataText3}</p>
            </div>

            <h3 className="pb-legal-h3">{ui.cookies}</h3>
            <div className="pb-legal-body"><p>{ui.cookiesText}</p></div>

            <h3 className="pb-legal-h3">{ui.liability}</h3>
            <div className="pb-legal-body">
              <p>{ui.liabText1}</p>
              <p>{ui.liabText2}</p>
            </div>
          </LegalSection>

          <div className="pb-sep" style={{ margin: '48px 0' }} />

          {/* ── Section CGU ── */}
          <LegalSection id={ui.sections.cgu.id} title={ui.sections.cgu.title}>
            <div className="pb-legal-body" style={{ marginBottom: 32 }}>
              <p>{ui.cguIntro}</p>
            </div>

            {ui.articles.map(article => (
              <Article key={article.num} num={article.num} title={article.title}>
                {article.text && article.text.map((t, i) => <p key={i}>{t}</p>)}
                {article.intro && <p>{article.intro}</p>}
                {article.list && (
                  <ul className="pb-legal-list">
                    {article.list.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
              </Article>
            ))}
          </LegalSection>

          <div className="pb-legal-footer-note">
            {ui.footer(year, LEGAL.siren, LEGAL.address)}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default function LegalPage() {
  return (
    <LanguageProvider>
      <LegalContent />
    </LanguageProvider>
  );
}
